import type { CalculatorInputs } from '@/lib/calculator'
import CalculatorInputsPanel from '@/components/calculator/CalculatorInputsPanel'
import CalculatorResultsPanel from '@/components/calculator/CalculatorResultsPanel'
import PrintableChecklist from '@/components/calculator/PrintableChecklist'
import { AdSlot, PremiumBundleCTA, RetailerAffiliateCards } from '@/components/content/MonetizationCards'
import SeoContent from '@/components/content/SeoContent'
import { calculate, decodeInputsFromParams, DEFAULT_INPUTS, encodeInputsToParams } from '@/lib/calculator'
import {GlassWater} from 'lucide-react'
import { useMemo, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'

function Header() {
  return (
    <header className="sticky top-0 z-30 bg-[#FAF8F5]/95 backdrop-blur border-b border-[#D4AF37]/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2" aria-label="Wedding Alcohol Calculator home">
          <GlassWater className="w-6 h-6 text-[#6B1D2F]" aria-hidden="true" />
          <span className="font-serif font-bold text-lg text-[#6B1D2F]">Wedding Alcohol Calculator</span>
        </a>
        <nav className="hidden sm:flex items-center gap-6 text-sm font-medium text-[#222222]" aria-label="Section navigation">
          <a href="#calculator" className="hover:text-[#6B1D2F] transition-colors">Calculator</a>
          <a href="#average-cost" className="hover:text-[#6B1D2F] transition-colors">Costs</a>
          <a href="#faq" className="hover:text-[#6B1D2F] transition-colors">FAQ</a>
        </nav>
      </div>
    </header>
  )
}

function Footer() {
  return (
    <footer className="bg-[#222222] text-gray-400 text-sm py-8 mt-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row justify-between gap-2">
        <span>&copy; {new Date().getFullYear()} Wedding Alcohol Calculator. All estimates are for planning purposes only.</span>
        <span>Please drink responsibly.</span>
      </div>
    </footer>
  )
}

function Home() {
  const [inputs, setInputs] = useState<CalculatorInputs>(() => decodeInputsFromParams(window.location.search) || DEFAULT_INPUTS)

  const results = useMemo(() => calculate(inputs), [inputs])

  const handleChange = <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: value }))
  }

  const handlePrint = () => window.print()

  const handleShare = () => {
    const params = encodeInputsToParams(inputs)
    const url = `${window.location.origin}${window.location.pathname}?${params}`
    navigator.clipboard.writeText(url).then(() => toast.success('Shareable link copied!'))
    window.history.replaceState(null, '', `?${params}`)
  }

  return (
    <div id="top" className="min-h-screen bg-[#FAF8F5] text-[#222222]">
      <Toaster position="top-center" />
      <Header />

      <main>
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-4">
          <AdSlot label="Top Header" />
        </section>

        <section id="calculator" className="max-w-6xl mx-auto px-4 sm:px-6 py-6 scroll-mt-20">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#6B1D2F]">
              The Ultimate Wedding Alcohol &amp; Drink Calculator
            </h1>
            <p className="mt-3 text-gray-600">
              Enter your guest count and preferences below for an instant, accurate breakdown of wine, beer,
              spirits, champagne, mixers, ice, and total cost.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 rounded-2xl bg-white shadow-xl p-6 sm:p-8 border border-[#D4AF37]/15 h-fit">
              <CalculatorInputsPanel inputs={inputs} onChange={handleChange} />
            </div>
            <div className="lg:col-span-3">
              <CalculatorResultsPanel results={results} onPrint={handlePrint} onShare={handleShare} />
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <AdSlot label="Mid-Content" />
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <RetailerAffiliateCards />
        </section>

        <SeoContent />

        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
          <PremiumBundleCTA />
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <AdSlot label="Sidebar / Sticky Bottom" />
        </section>
      </main>

      <Footer />
      <PrintableChecklist inputs={inputs} results={results} />
    </div>
  )
}

export default Home
